import { debouncePromiseWithCancelMethod } from '@/helpers/utils'
import { logger } from '@/logger/createLogger'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const DEFAULT_DEBOUNCE_MS = 300

/**
 * @param currentValue - The current value of the state
 * @param onUpdate - The function to update the state
 * @param debounceMs - The debounce time in milliseconds
 * @returns An object containing the optimistic value and a function to update the state
 */
export const useOptimisticUpdateWithPromise = <TValue>(
  currentValue: TValue,
  onUpdate: <UValue extends TValue>(value: UValue) => Promise<void>,
  debounceMs = DEFAULT_DEBOUNCE_MS,
) => {
  const [optimisticValue, setOptimisticValue] = useState(currentValue)
  const onUpdateRef = useRef(onUpdate)
  const currentValueRef = useRef(currentValue)

  // Always use the latest callback on re-render
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  const debouncedUpdate = useMemo(
    () =>
      debouncePromiseWithCancelMethod(
        (value: TValue) => onUpdateRef.current(value),
        debounceMs,
      ),
    [debounceMs],
  )

  // Keep refs in sync
  useEffect(() => {
    currentValueRef.current = currentValue

    return () => {
      // Cancel the debounce function when the component unmounts
      // or when currentValue, or debouncedUpdate changes
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate, currentValue])

  // Sync optimistic value when current value changes (from server)
  useEffect(() => {
    setOptimisticValue(currentValue)
  }, [currentValue])

  const handleUpdate = useCallback(
    (newValue: TValue) => {
      // Immediate optimistic update
      setOptimisticValue(newValue)

      // Debounced server update
      debouncedUpdate.debounce(newValue).catch(error => {
        logger.error(
          'useOptimisticUpdateWithPromise - Error updating state:',
          error,
        )
        // Only revert if we're still showing the failed value
        setOptimisticValue(current =>
          current === newValue ? currentValueRef.current : current,
        )
      })
    },
    [debouncedUpdate],
  )

  return {
    handleUpdate,
    optimisticValue,
  }
}
